
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as entity from '../entity'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DrawingStorageChartRequest struct
 */
class DrawingStorageChartRequest {
  /**
   * Initialize struct
   * @param {string=} symbol
   * @param {number=} version
   * @param {string=} chartSettings
   * @constructor
   */
  constructor (symbol = null, version = null, chartSettings = null) {
    this.symbol = symbol
    this.version = version
    this.chartSettings = chartSettings
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DrawingStorageChartRequest}
   * @param {!DrawingStorageChartRequest} other Other struct
   * @returns {!DrawingStorageChartRequest} This struct
   */
  copy (other) {
    if (other.symbol != null) {
      this.symbol = other.symbol
    } else {
      this.symbol = null
    }
    if (other.version != null) {
      this.version = UInt64.fromNumber(other.version)
    } else {
      this.version = null
    }
    if (other.chartSettings != null) {
      this.chartSettings = other.chartSettings
    } else {
      this.chartSettings = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DrawingStorageChartRequest}
   * @returns {!DrawingStorageChartRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DrawingStorageChartRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DrawingStorageChartRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DrawingStorageChartRequest}
   * @param {!DrawingStorageChartRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DrawingStorageChartRequest)) {
      throw new TypeError('Instance of DrawingStorageChartRequest is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DrawingStorageChartRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      symbol: ((this.symbol != null) ? this.symbol : null),
      version: ((this.version != null) ? this.version.toNumber() : null),
      chartSettings: ((this.chartSettings != null) ? this.chartSettings : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DrawingStorageChartRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DrawingStorageChartRequest} other Object value
   * @returns {!DrawingStorageChartRequest} Created struct
   */
  static fromObject (other) {
    return new DrawingStorageChartRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DrawingStorageChartRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DrawingStorageChartRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DrawingStorageChartRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 10003
  }
}

export { DrawingStorageChartRequest };

/**
 * Fast Binary Encoding DrawingStorageChartRequest field model
 */
class FieldModelDrawingStorageChartRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.symbol = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, 4 + 4), buffer, 4 + 4)
    this.version = new fbe.FieldModelOptional(new fbe.FieldModelUInt64(buffer, this.symbol.fbeOffset + this.symbol.fbeSize), buffer, this.symbol.fbeOffset + this.symbol.fbeSize)
    this.chartSettings = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.version.fbeOffset + this.version.fbeSize), buffer, this.version.fbeOffset + this.version.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.symbol.fbeSize + this.version.fbeSize + this.chartSettings.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.symbol.fbeExtra + this.version.fbeExtra + this.chartSettings.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDrawingStorageChartRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 10003
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.symbol.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.symbol.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.symbol.fbeSize

    if ((fbeCurrentSize + this.version.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.version.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.version.fbeSize

    if ((fbeCurrentSize + this.chartSettings.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.chartSettings.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.chartSettings.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!DrawingStorageChartRequest} fbeValue Default value, defaults is new DrawingStorageChartRequest()
   * @returns {!DrawingStorageChartRequest} DrawingStorageChartRequest value
   */
  get (fbeValue = new DrawingStorageChartRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!DrawingStorageChartRequest} fbeValue DrawingStorageChartRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.symbol.fbeSize) <= fbeStructSize) {
      fbeValue.symbol = this.symbol.get()
    } else {
      fbeValue.symbol = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.symbol.fbeSize

    if ((fbeCurrentSize + this.version.fbeSize) <= fbeStructSize) {
      fbeValue.version = this.version.get()
    } else {
      fbeValue.version = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.version.fbeSize

    if ((fbeCurrentSize + this.chartSettings.fbeSize) <= fbeStructSize) {
      fbeValue.chartSettings = this.chartSettings.get()
    } else {
      fbeValue.chartSettings = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.chartSettings.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDrawingStorageChartRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {!DrawingStorageChartRequest} fbeValue DrawingStorageChartRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDrawingStorageChartRequest}
   * @param {DrawingStorageChartRequest} fbeValue DrawingStorageChartRequest value
   */
  setFields (fbeValue) {
    this.symbol.set(fbeValue.symbol)
    this.version.set(fbeValue.version)
    this.chartSettings.set(fbeValue.chartSettings)
  }
}

export { FieldModelDrawingStorageChartRequest };

/**
 * Fast Binary Encoding DrawingStorageChartRequest model
 */
class DrawingStorageChartRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDrawingStorageChartRequest(this.buffer, 4)
  }

  /**
   * Get the DrawingStorageChartRequest model
   * @this {!DrawingStorageChartRequestModel}
   * @returns {!FieldModelDrawingStorageChartRequest} model DrawingStorageChartRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DrawingStorageChartRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DrawingStorageChartRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DrawingStorageChartRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DrawingStorageChartRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDrawingStorageChartRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DrawingStorageChartRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DrawingStorageChartRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DrawingStorageChartRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DrawingStorageChartRequestModel}
   * @param {!DrawingStorageChartRequest} value DrawingStorageChartRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DrawingStorageChartRequestModel}
   * @param {!DrawingStorageChartRequest} value DrawingStorageChartRequest value, defaults is new DrawingStorageChartRequest()
   * @return {!object} Deserialized DrawingStorageChartRequest value and its size
   */
  deserialize (value = new DrawingStorageChartRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DrawingStorageChartRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DrawingStorageChartRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DrawingStorageChartRequestModel}
   * @param {!number} prev Previous DrawingStorageChartRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DrawingStorageChartRequestModel };
